import Phaser from 'phaser';
import type {
  ParallaxPreset,
  ParallaxBlendMode,
} from '@/config/parallax.config';

/**
 * ParallaxBackgroundSystem — warstwa(y) tła dryfujące wolniej niż świat.
 *
 * Daje głębię w widoku z góry (gdzie nie ma horyzontu) przez camera-fixed
 * `TileSprite` na rozmiar viewportu, którego `tilePosition` przesuwamy co klatkę
 * o `cam.scroll × parallaxFactor`. Współczynnik <1 sprawia, że warstwa „zostaje
 * w tyle" za światem — klasyczny parallax bez szpar na krawędziach (TileSprite
 * powtarza teksturę w nieskończoność, niezależnie od rozmiaru świata i resize).
 *
 * **scrollFactor=0:** TileSprite renderuje się w SCREEN coords i nie przewija się
 * sam z kamerą — ruch daje TYLKO `tilePosition` (kontrolowany przez nas). Dzięki
 * temu mamy pełną kontrolę nad „prędkością" warstwy względem świata.
 *
 * **Depth:** typowo −95 (nad ziemią −100, pod dekoracjami −50), żeby barwić/cieniować
 * ziemię, ale NIE zasłaniać petów/gracza/destruktibli (które są wyżej).
 *
 * **Autodryf:** `driftX/Y` dodaje powolny ruch własny (chmury suną nawet gdy gracz
 * stoi). Wyrażony w px/s ekranu (skompensowany o `tileScale`).
 *
 * **Cleanup:** `destroy()` niszczy wszystkie TileSprite'y. Wołane z
 * `WorldScene.cleanup()` (SHUTDOWN) + reset block na początku create()
 * (zombie-state pattern, identycznie jak ambientFx/atmosphere).
 *
 * **Edge cases:**
 *  - Brak tekstury warstwy → warn + skip tej warstwy (gra działa, parallax po
 *    prostu się nie pokazuje dopóki PNG nie zostanie wygenerowany).
 */
export class ParallaxBackgroundSystem {
  private readonly preset: ParallaxPreset;
  private readonly startTimeMs: number;

  /** Każda warstwa: TileSprite + jej config (potrzebny w per-frame update). */
  private readonly items: Array<{
    sprite: Phaser.GameObjects.TileSprite;
    parallaxFactor: number;
    driftX: number;
    driftY: number;
    tileScale: number;
  }> = [];

  constructor(
    private readonly scene: Phaser.Scene,
    preset: ParallaxPreset,
  ) {
    this.preset = preset;
    this.startTimeMs = scene.time.now;
    this.build();
    // Pierwsze ułożenie pod aktualny viewport — żeby przed pierwszym update()
    // nic nie błysnęło w (0,0) lewym górnym rogu.
    this.layout(0);
  }

  private build(): void {
    const cam = this.scene.cameras.main;

    for (const layer of this.preset.layers) {
      if (!this.scene.textures.exists(layer.textureKey)) {
        console.warn(
          `[Parallax] Pominięto warstwę — brak tekstury ${layer.textureKey}`,
        );
        continue;
      }

      const tileScale = layer.tileScale ?? 1;
      const sprite = this.scene.add
        .tileSprite(cam.width / 2, cam.height / 2, cam.width, cam.height, layer.textureKey)
        .setScrollFactor(0)
        .setDepth(layer.depth)
        .setAlpha(layer.alpha)
        .setTileScale(tileScale);

      if (layer.blendMode) sprite.setBlendMode(toPhaserBlend(layer.blendMode));
      if (layer.tint !== undefined) sprite.setTint(layer.tint);

      this.items.push({
        sprite,
        parallaxFactor: layer.parallaxFactor,
        driftX: layer.driftX,
        driftY: layer.driftY,
        tileScale,
      });
    }
  }

  /**
   * Per-frame tick — wołany z `WorldScene.update(time, dt)`.
   * Przelicza pozycję/rozmiar pod aktualny viewport (resize-safe) i ustawia
   * tilePosition na bazie scrolla kamery + autodryfu.
   */
  update(time: number): void {
    this.layout((time - this.startTimeMs) / 1000);
  }

  /** Układa WSZYSTKIE warstwy z aktualnego viewportu + scrolla + elapsed (sek). */
  private layout(elapsedSec: number): void {
    const cam = this.scene.cameras.main;
    const w = cam.width;
    const h = cam.height;

    for (const item of this.items) {
      // Wycentruj + dopasuj do viewportu (resize-safe).
      item.sprite.setPosition(w / 2, h / 2).setSize(w, h);

      // tilePosition w pikselach ŹRÓDŁA tekstury → dzielimy przez tileScale,
      // żeby parallaxFactor i drift działały w px EKRANU (niezależnie od skali).
      const s = item.tileScale || 1;
      item.sprite.tilePositionX = (cam.scrollX * item.parallaxFactor + elapsedSec * item.driftX) / s;
      item.sprite.tilePositionY = (cam.scrollY * item.parallaxFactor + elapsedSec * item.driftY) / s;
    }
  }

  /**
   * Niszczy wszystkie TileSprite'y. Idempotent — wielokrotne wywołanie jest
   * no-opem (Phaser destroy() po pierwszym razie nic nie robi).
   */
  destroy(): void {
    for (const item of this.items) {
      item.sprite.destroy();
    }
    this.items.length = 0;
  }
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function toPhaserBlend(mode: ParallaxBlendMode): Phaser.BlendModes {
  switch (mode) {
    case 'ADD':      return Phaser.BlendModes.ADD;
    case 'SCREEN':   return Phaser.BlendModes.SCREEN;
    case 'MULTIPLY': return Phaser.BlendModes.MULTIPLY;
    case 'NORMAL':
    default:         return Phaser.BlendModes.NORMAL;
  }
}
